// 報價單列表
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, money, fmtDate, todayStr, addDays, QT_STATUS } from '../api.js';
import { StatusBadge, CategoryTag, Loading, Empty, PageHead, Modal, Field, useToast, useConfirm } from '../ui.jsx';

// 由 PDF 直接建立報價單 (Port-in) — 揀客戶 + 上傳 PDF, 自動建草稿報價 + RA 版本附件
function PortInModal({ onClose, done }) {
  const { toast } = useToast();
  const [customers, setCustomers] = useState([]);
  const [f, setF] = useState({ customer_id: '', title: '', manual_total: '' });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/customers').then(r => setCustomers(Array.isArray(r) ? r : (r.customers || []))).catch(() => {}); }, []);
  const go = async () => {
    if (!f.customer_id) { toast('請先選擇客戶', 'err'); return; }
    if (!file) { toast('請選擇報價 PDF 檔案', 'err'); return; }
    setBusy(true);
    try {
      const q = await api.post('/quotations', {
        customer_id: Number(f.customer_id), category: 'INSTALLATION', title: f.title || '',
        issue_date: todayStr(), valid_until: addDays(todayStr(), 30),
        items: [], manual_total: f.manual_total === '' ? null : Number(f.manual_total),
        notes: 'PDF port-in',
      });
      const v = await api.post('/quotations/' + q.id + '/versions', { port_in: true });
      const b64 = await new Promise((ok, no) => { const rd = new FileReader(); rd.onload = () => ok(rd.result); rd.onerror = no; rd.readAsDataURL(file); });
      await api.post('/versions/' + v.id + '/pdf', { filename: file.name, mime: file.type || 'application/pdf', data_base64: b64, port_in: true });
      toast('PDF 已匯入，報價單已建立 ✔');
      done(q.id);
    } catch (e) { toast(e.message, 'err'); setBusy(false); }
  };
  return (
    <Modal title="📎 由 PDF 建立報價單 (Port-in)" onClose={onClose}>
      <p className="muted">將現有報價單 PDF 存入系統：揀客戶 → 上傳 PDF，即自動建立草稿報價單（附帶 RA 版本附件）。總額可以而家填，或者之後喺編輯器用「手動總額覆蓋」填。</p>
      <Field label="客戶 *">
        <select className="select" value={f.customer_id} onChange={e => setF(x => ({ ...x, customer_id: e.target.value }))}>
          <option value="">— 選擇客戶 —</option>
          {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="工程名稱 Site (可選)">
        <input className="input" value={f.title} onChange={e => setF(x => ({ ...x, title: e.target.value }))} placeholder="例如 FCU Installation Work" />
      </Field>
      <Field label="總額 HKD$ (可選 — 之後可改)">
        <input className="input num-input" type="number" min="0" step="0.01" value={f.manual_total} onChange={e => setF(x => ({ ...x, manual_total: e.target.value }))} placeholder="留空 = 稍後喺編輯器填" />
      </Field>
      <Field label="報價 PDF 檔案 *">
        <input className="input" type="file" accept="application/pdf,image/*" onChange={e => setFile(e.target.files[0] || null)} />
      </Field>
      <div className="row end" style={{ gap: 10, marginTop: 14 }}>
        <button className="btn" onClick={onClose}>取消</button>
        <button className="btn primary" disabled={busy} onClick={go}>{busy ? '匯入中…' : '📎 匯入並建立'}</button>
      </div>
    </Modal>
  );
}

export default function Quotations() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [confirm, confirmNode] = useConfirm();
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState('');
  const [cat, setCat] = useState('');
  const [q, setQ] = useState('');
  const [portIn, setPortIn] = useState(false);
  const load = () => {
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    if (cat) p.set('category', cat);
    if (q) p.set('q', q);
    api.get(`/quotations?${p}`).then(setRows);
  };
  useEffect(load, [status, cat, q]);
  const del = (row) => confirm(`刪除報價單 ${row.quote_no}？`, async () => {
    try { await api.del(`/quotations/${row.id}`); toast('已刪除'); setRows(l => l.filter(x => x.id !== row.id)); }
    catch (e) { toast(e.message, 'err'); }
  }, { danger: true, okLabel: '刪除' });
  const convertFromList = async (row) => {
    try {
      const fresh = await api.get('/quotations/' + row.id);
      const r = await api.post('/quotations/' + row.id + '/convert', {
        order_date: new Date().toISOString().slice(0, 10),
        items: fresh.items,
        remarks: fresh.remarks || [],
      });
      toast('已轉訂單 ' + r.order_no + ' ✔');
      load();
    } catch (e) { toast(e.message, 'err'); }
  };
  const dup = async (row) => {
    try { const r = await api.post(`/quotations/${row.id}/duplicate`); toast('已複製 — 可修改後另存'); nav(`/quotations/${r.id}`); }
    catch (e) { toast(e.message, 'err'); }
  };

  return (
    <div>
      <PageHead icon="📄" title="報價單 Quotations" desc="INSTALLATION 安裝 · MAINTENANCE 保養 · E-CALL 緊急維修 — 報價確認後一鍵轉訂單"
        actions={<>
          <div className="search-box"><input className="input" placeholder="搜尋編號/客戶…" value={q} onChange={e => setQ(e.target.value)} /></div>
          <button className="btn" onClick={() => setPortIn(true)}>📎 PDF 匯入</button>
          <button className="btn primary" onClick={() => nav('/quotations/new')}>➕ 新報價單</button>
        </>} />
      <div className="row mb">
        <div className="pill-filter">
          <button className={`pill ${!status ? 'active' : ''}`} onClick={() => setStatus('')}>全部</button>
          {Object.entries(QT_STATUS).map(([k, v]) => (
            <button key={k} className={`pill ${status === k ? 'active' : ''}`} onClick={() => setStatus(k)}>{v.label}</button>
          ))}
        </div>
        <div className="pill-filter">
          <button className={`pill ${!cat ? 'active' : ''}`} onClick={() => setCat('')}>全部類別</button>
          <button className={`pill ${cat === 'INSTALLATION' ? 'active' : ''}`} onClick={() => setCat('INSTALLATION')}>🔧 安裝</button>
          <button className={`pill ${cat === 'MAINTENANCE' ? 'active' : ''}`} onClick={() => setCat('MAINTENANCE')}>🛡️ 保養</button>
          <button className={`pill ${cat === 'ECALL' ? 'active' : ''}`} onClick={() => setCat('ECALL')}>🚨 E-Call</button>
        </div>
      </div>
      <div className="card">
        {rows === null ? <Loading /> : rows.length === 0 ? <Empty icon="📄" text="沒有符合的報價單" >
          <button className="btn primary mt" onClick={() => nav('/quotations/new')}>➕ 開第一張報價</button>
        </Empty> : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead><tr><th>編號</th><th>客戶</th><th>項目</th><th>類別</th><th>日期</th><th className="num">金額</th><th>狀態</th><th></th></tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="clickable" onClick={() => nav(`/quotations/${r.id}`)}>
                    <td className="strong">{r.quote_no}</td>
                    <td>{r.customer_name}</td>
                    <td className="sub" style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title || '—'}</td>
                    <td><CategoryTag value={r.category} /></td>
                    <td>{fmtDate(r.issue_date)}</td>
                    <td className="num strong">{money(r.total)}</td>
                    <td><StatusBadge map={QT_STATUS} value={r.status} /></td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="row" style={{ flexWrap: 'nowrap' }}>
                        {['draft','sent','accepted'].includes(r.status) && <button className="btn sm violet" title="轉為工程訂單" onClick={() => convertFromList(r)}>🏗️ 轉單</button>}
                          {r.status !== 'converted' && <button className="btn sm" onClick={() => dup(r)}>⧉</button>}
                        {api.user?.role === 'admin' && ['draft', 'sent', 'accepted'].includes(r.status) && <button className="btn sm coral" onClick={() => del(r)}>🗑️</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {confirmNode}
      {portIn && <PortInModal onClose={() => setPortIn(false)} done={(id) => { setPortIn(false); nav('/quotations/' + id); }} />}
    </div>
  );
}
